import useToastContext from '@contexts/ToastContext/hook';
import { Box, CircularProgress } from '@mui/material';
import { Typography } from '@vibTheme/components/_base/Typography';
import { parseFilters } from '@utils/parseFilters';
import parseMoneyValue from '@utils/parseMoneyValue';
import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import styles from '@styles/Card.module.css';
import { findSalesHistory } from '@api/reports';
import {
  SalesHistory,
  SalesHistoryFilters,
} from '@interfaces/reports/salesHistory';
import {
  hasPreviousYearPair,
  PairedSalesHistory,
  pairPreviousYearSales,
} from '@utils/pairPreviousYearSales';

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: PairedSalesHistory }[];
}) => {
  // Uma caixa por mês: o recharts entrega uma entrada de payload por série, ambas do mesmo mês.
  const point = payload?.[0]?.payload;

  if (!active || !point) return null;

  const salesValue = parseMoneyValue(point.salesValue) ?? '';
  const groupProfit = parseMoneyValue(point.groupProfit) ?? '';

  return (
    <Box className="custom-tooltip">
      <Box
        style={{
          display: 'inline-block',
          padding: 10,
          borderRadius: '5px',
        }}
        className="bg-primary/80"
      >
        <Typography className="text-white text-xs">{`Ano: ${point.year}`}</Typography>
        <Typography className="text-white text-xs">{`Mês: ${point.month}`}</Typography>
        <Typography className="text-white text-xs">{`Valor: ${salesValue}`}</Typography>
        <Typography className="text-white text-xs">{`Quantidade: ${point.salesCount}`}</Typography>
        <Typography className="text-white text-xs">{`Lucro: ${groupProfit}`}</Typography>
        {/*
          A comparação é dita por extenso, com o mês e o ano na frase. Assim ela não depende de ninguém
          decorar qual cor é qual, e a ausência do dado é DITA em vez de virar um zero.
        */}
        <Typography className="text-white text-xs">
          {point.previousYearSalesValue === null
            ? `Sem dados de ${point.month}/${point.year - 1}`
            : `${point.month}/${point.year - 1}: ${parseMoneyValue(
                point.previousYearSalesValue,
              )}`}
        </Typography>
      </Box>
    </Box>
  );
};

type Props = { filters: SalesHistoryFilters };

export function SalesHistoryChart(props: Props) {
  const { filters } = props;

  const toast = useToastContext();
  const [data, setData] = useState<SalesHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    /**
     * Protege contra requisição superada.
     *
     * Trocar de comunidade ou de função dispara uma nova busca com a anterior ainda em voo, e sem esta
     * guarda quem termina por último é quem manda: uma resposta antiga mais lenta sobrescrevia a nova,
     * e o gráfico ficava mostrando a seleção anterior até alguém mexer no filtro de novo.
     *
     * O catch importa tanto quanto o then. `toast` vem do provider global, que continua montado mesmo
     * quando este componente não está, então uma requisição já descartada que falhasse anunciava erro
     * de carregamento por cima de uma seleção que tinha carregado bem.
     */
    let active = true;

    if (!filters.botConfigIds.length) {
      setData([]);
      // Explícito: desmarcar todas as comunidades invalida a requisição em voo, então o finally dela
      // não limpa mais o loading, e sem isto o gráfico giraria para sempre.
      setIsLoading(false);
      return () => {
        active = false;
      };
    }

    setIsLoading(true);
    findSalesHistory(parseFilters(filters))
      .then((response) => {
        if (active) setData(response.data);
      })
      .catch((error) => {
        if (!active) return;
        toast.handleError(error, 'Não foi possível carregar os registros');
        setData([]);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line
  }, [filters]);

  const chartData = pairPreviousYearSales(data ?? []);
  const hasPreviousYear = hasPreviousYearPair(chartData);
  return (
    <section className={styles.containerHistory}>
      {isLoading ? (
        <Box className="flex justify-center items-center h-[277px]">
          <CircularProgress />
        </Box>
      ) : (
        // Essas duas divs pai são necessárias pro ResponsiveContainer não demorar
        // uma vida inteira pra se redimensionar
        // https://github.com/recharts/recharts/issues/1767#issuecomment-598607012
        <div className="relative w-full h-full">
          <div className="absolute w-full h-full t-0 l-0">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid
                  strokeOpacity={1}
                  vertical={false}
                  strokeDasharray="6"
                />
                <XAxis
                  dataKey="date"
                  tick={{
                    fontFamily: 'system-ui',
                    fontWeight: '400',
                    fontSize: 12,
                    fill: '#B6BAC3',
                  }}
                  stroke="#EEEEEE"
                />
                {/*
                  Sem `dataKey`: o domínio tem que cobrir as DUAS séries. Preso à série atual, um ano
                  anterior mais forte era desenhado para fora do topo do gráfico.
                */}
                <YAxis
                  type="number"
                  tickFormatter={(label: number) => `R$${label / 1000}k`}
                  tick={{
                    fontFamily: 'system-ui',
                    fontWeight: '400',
                    fontSize: 12,
                    fill: '#B6BAC3',
                  }}
                  stroke="#EEEEEE"
                />
                <Tooltip content={<CustomTooltip />} />
                {hasPreviousYear && (
                  <Legend
                    verticalAlign="top"
                    align="right"
                    height={28}
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 12, color: '#374151' }}
                  />
                )}
                {/*
                  O ano anterior vem primeiro, à esquerda de cada mês: dentro do par as barras ficam em
                  ordem cronológica, do mesmo jeito que os meses ficam ao longo do eixo.
                */}
                {hasPreviousYear && (
                  <Bar
                    dataKey="previousYearSalesValue"
                    name="Mesmo mês do ano anterior"
                    fill="#B6BAC3"
                    barSize={12}
                  />
                )}
                <Bar
                  dataKey="salesValue"
                  name="Vendas do mês"
                  fill="#2E3192"
                  barSize={12}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </section>
  );
}
